import { resolvePath, normalizePath } from "./fileSystem";
import info from "../Info";

const SERVER_URL = process.env.REACT_APP_SERVER_PUBLIC_URL || "https://portfoliowebsite-bm23.onrender.com";
let chatWarmupPromise = null;
const CHAT_SESSION_STORAGE_KEY = "portfolio-chat-session-id";

const getChatSessionId = () => {
  if (typeof window === "undefined") return "server-session";

  const existing = window.sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
  if (existing) return existing;

  const generated =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  window.sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, generated);
  return generated;
};

const helpText = [
  "Available commands:",
  "",
  "  help          Show this help message",
  "  ls            List files in current directory",
  "  ls -a         List all files (including hidden)",
  "  cd <dir>      Change directory",
  "  cat <file>    Display file contents",
  "  pwd           Print working directory",
  "  whoami        Display current user",
  "  contact       Show contact information",
  "  echo <text>   Echo text back",
  "  clear         Clear the terminal",
  "",
  "Anything else you type will be answered by Kevin's AI assistant.",
  "Tip: Try 'ls -a' to discover hidden files!",
  "Tip: Run 'cat welcome.txt' to see the terminal intro again.",
];

export const executeCommand = (input, currentPath, fileSystem) => {
  const trimmed = input.trim();
  if (!trimmed) return { output: [] };

  const parts = trimmed.split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const args = parts.slice(1);

  switch (cmd) {
    case "help":
      return { output: helpText };

    case "ls": {
      const showHidden = args.includes("-a");
      const node = resolvePath(fileSystem, currentPath);
      if (!node || node.type !== "dir") {
        return { output: ["Not a directory"] };
      }
      const entries = Object.keys(node.children)
        .filter((name) => showHidden || !name.startsWith("."))
        .sort()
        .map((name) => {
          const child = node.children[name];
          return child.type === "dir" ? name + "/" : name;
        });
      return { output: entries.length > 0 ? entries : ["(empty directory)"] };
    }

    case "cd": {
      if (args.length === 0 || args[0] === "~") {
        return { output: [], newPath: [] };
      }
      const target = args[0];
      const newParts = normalizePath(currentPath, target);
      const node = resolvePath(fileSystem, newParts);
      if (!node) {
        return { output: [`cd: no such directory: ${target}`] };
      }
      if (node.type !== "dir") {
        return { output: [`cd: not a directory: ${target}`] };
      }
      return { output: [], newPath: newParts };
    }

    case "cat": {
      if (args.length === 0) {
        return { output: ["Usage: cat <filename>"] };
      }
      const filePath = args[0];
      const fileParts = normalizePath(currentPath, filePath);
      const node = resolvePath(fileSystem, fileParts);
      if (!node) {
        return { output: [`cat: ${filePath}: No such file or directory`] };
      }
      if (node.type === "dir") {
        return { output: [`cat: ${filePath}: Is a directory`] };
      }
      return { output: node.content.split("\n") };
    }

    case "pwd": {
      const path = "~" + (currentPath.length > 0 ? "/" + currentPath.join("/") : "");
      return { output: [path] };
    }

    case "whoami":
      return { output: ["kevin-philip"] };

    case "contact":
      return {
        output: [
          `Email: ${info.email}`,
          `Phone: ${info.phone}`,
          `LinkedIn: ${info.linkedin}`,
          `GitHub: ${info.github}`,
          `Website: ${info.website}`,
        ],
      };

    case "echo":
      return { output: [args.join(" ")] };

    case "clear":
      return { output: [], clear: true };

    default:
      return {
        output: [],
        stream: (onUpdate, onDone) => streamAIResponse(trimmed, onUpdate, onDone),
      };
  }
};

export const warmUpChatServer = () => {
  if (chatWarmupPromise) return chatWarmupPromise;

  // Best-effort wake-up to reduce first-message cold start latency.
  chatWarmupPromise = fetch(`${SERVER_URL}/health`, {
    method: "GET",
    mode: "cors",
  })
    .then(() => true)
    .catch(() => false);

  return chatWarmupPromise;
};

const streamAIResponse = async (question, onUpdate, onDone) => {
  try {
    const response = await fetch(`${SERVER_URL}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: question,
        sessionId: getChatSessionId(),
      }),
    });

    if (!response.ok) {
      let errorMsg = "Error: Could not reach AI. Try again later.";
      try {
        const errorData = await response.json();
        if (errorData?.error) {
          errorMsg = errorData.error;
        }
      } catch (e) {
        // keep generic fallback
      }
      onDone(errorMsg);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n").filter((l) => l.startsWith("data: "));

      for (const line of lines) {
        const data = line.slice(6);
        if (data === "[DONE]") {
          onDone(fullText);
          return;
        }
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            onDone(parsed.error);
            return;
          }
          if (parsed.content) {
            fullText += parsed.content;
            onUpdate(fullText);
          }
        } catch (e) {
          // skip malformed chunks
        }
      }
    }
    onDone(fullText);
  } catch (err) {
    onDone("Error: Could not connect to server. Try again later.");
  }
};
